import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Message Sent!",
          description: data.message || "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://customer-assets.emergentagent.com/job_ai-innovation-3/artifacts/15c9utzi_sanjuanventures_A_3D_illustration_showing_four_people_two_men_151885da-9c93-4962-8599-eb029be75ac4_3.mp4" type="video/mp4" />
        </video>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            Let's <span className="inline-block">
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '0s'}}>B</span>
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '0.4s'}}>u</span>
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '0.8s'}}>i</span>
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '1.2s'}}>l</span>
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '1.6s'}}>d</span>
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent glow-letter" style={{animationDelay: '2s'}}>!</span>
            </span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6 drop-shadow-lg">Send us a message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-md">
                  Full Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full placeholder:text-pink-400"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-md">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full placeholder:text-pink-400"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-md">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full placeholder:text-pink-400"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2 drop-shadow-md">
                  Your Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full min-h-[150px] placeholder:text-pink-400"
                  placeholder="Tell us about your project..."
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold py-6 text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <Mail className="w-8 h-8 text-pink-400 drop-shadow-lg flex-shrink-0" />
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1 drop-shadow-lg">Email Us</h4>
                <p className="text-gray-800 drop-shadow-md">admin@lettherebe.ai</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Phone className="w-8 h-8 text-violet-400 drop-shadow-lg flex-shrink-0" />
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1 drop-shadow-lg">Call Us</h4>
                <p className="text-gray-800 drop-shadow-md">202-441-7683</p>
                <p className="text-sm text-gray-700 mt-1 drop-shadow-md">Mon-Fri 9am-6pm EST</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <MapPin className="w-8 h-8 text-yellow-400 drop-shadow-lg flex-shrink-0" />
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1 drop-shadow-lg">Visit Us</h4>
                <p className="text-gray-800 drop-shadow-md">San Francisco, CA</p>
                <p className="text-gray-800 drop-shadow-md">United States</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;